import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { find as _find, get as _get, has as _has } from 'lodash';
import { from, Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { APP_STATE, MAP_TRIGGER } from '../../+state/app-state/app-state.models';
import {
  SEARCH_TYPES,
  SearchContext,
  SearchedValue,
  SearchResult
} from '../../+state/service-locator/service-locator.models';
import { Geocoder } from '../../models/google-maps/geocode';
import { InfoWindow } from '../../models/google-maps/infoWindow';
import { MapMarker } from '../../models/google-maps/marker';
import { PulseOverlay } from '../../models/google-maps/overlay';
import { DEFAULT_MAP_OPTIONS, MAP_COLORS } from '../../models/map.models';
import { ImageService } from '../../services/image.service';

@Component({
  selector: 'drp-results-map',
  templateUrl: './results-map.component.html',
  styleUrls: ['./results-map.component.scss'],
  standalone: false
})
export class ResultsMapComponent implements OnChanges, AfterContentInit, AfterViewInit, OnDestroy {
  @Input() appState: APP_STATE;
  /**
   * List of shops as a result of a search event or a drag event
   * includes an index and the shop information
   */
  @Input() shopSearchResults: SearchResult[];

  /**
   * the value the user has searched
   */
  @Input() searchedValue: SearchedValue;

  /**
   * The type of search that was conducted - used to toggle the maps interactivity
   */
  @Input() searchType: SEARCH_TYPES;

  /**
   * shop the user has selected from the results
   */
  @Input() selectedShop: SearchResult;

  /**
   * Handles how the map is drawn based on what event the user has taken
   * Search, drag or paginate
   */
  @Input() currentMapRedrawTriggerState: MAP_TRIGGER;

  /**
   * Value to revert back to when the user drags the searchMarker outside of the US
   */
  @Input() lastSuccessfulSearchedValue: SearchedValue;

  /**
   * Feature toggle to enable / disable shop selection button on Info Window
   */
  @Input() searchContext: SearchContext;

  /**
   * Zip code of the location the search marker was dragged to
   */
  @Output() userRelocatedZipCode = new EventEmitter<any>();

  /**
   * Coordinates of the search marker after it was dragged
   */
  @Output() searchMarkerCoords = new EventEmitter<SearchedValue>();

  /**
   * Set the shop as selected if the shop marker was clicked on in the map
   */
  @Output() setMapShopSelection = new EventEmitter<SearchResult>();

  /**
   * Set the App State based on what results come back from the map interaction
   */
  @Output() setStateFromResults = new EventEmitter<APP_STATE>();

  /**
   * Set what action the user has taken on the map to handle the map redraw
   */
  @Output() setMapRedrawTriggerState = new EventEmitter<MAP_TRIGGER>();
  /**
   *  Set the value to revert back to when the user drags the searchMarker outside of the US
   */
  @Output() setLastSuccessfulSearchedValue = new EventEmitter<SearchedValue>();

  /**
   * Emit the selected shop out to the parent component
   */
  @Output() shopSelected = new EventEmitter<SearchResult>();

  @ViewChild('gmap', { static: true }) gmap: any;

  map: google.maps.Map;
  geocoder: any; // Drp Geocoder;
  searchMarker: MapMarker;
  contextValues = SearchContext;

  infoWindow: any; // google.maps.InfoWindow;
  shopMarkers = [];

  overlay: any;
  mapLoaded = false;

  private stop$ = new Subject<void>();

  constructor(
    private cd: ChangeDetectorRef,
    private images: ImageService
  ) {}

  ngAfterViewInit() {
    // Create the map
    this.map = new google.maps.Map(this.gmap.nativeElement, DEFAULT_MAP_OPTIONS);

    // Listen for tilesloaded event and fire mapLoaded() to set class for testing
    google.maps.event.addListenerOnce(this.map, 'tilesloaded', this.mapHasLoaded.bind(this));

    // Style the map based on the searchContext
    if (this.searchContext === SearchContext.DRP_SHOP_SEARCH) {
      this.map.mapTypes.set('styled_map', new google.maps.StyledMapType(MAP_COLORS));
      this.map.setMapTypeId('styled_map');
    }
    // Create the search marker
    this.createSearchMarker(this.searchType);

    // Create the overlay
    this.overlay = new PulseOverlay(this.map);

    // Instantiate the Drp Geocoder
    this.geocoder = new Geocoder();

    // Instantiate the InfoWindow
    this.infoWindow = new InfoWindow(this.map, { maxWidth: 320 });

    // Subscribe to shop selection events
    this.infoWindow
      .getSelectedShop$()
      .pipe(takeUntil(this.stop$))
      .subscribe((isSelected: boolean) => {
        if (isSelected) {
          // If there is a selected shop, emit that out as the user's selection
          this.shopSelected.emit(this.selectedShop);
        } else {
          // If the isSelected value is false, that means the user cancelled the infowindow
          this.setMapShopSelection.emit(null);
        }
      });

    // Update the map initially with the provided searchValue
    if (this.geocoder) {
      this.redrawMapOnSearch(this.searchedValue);
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  ngAfterContentInit() {
    this.cd.detectChanges();
  }

  /**
   * Set map markers when there are changes to the shopSearchResults
   * Set up map when map is ready
   */
  ngOnChanges(changes: SimpleChanges) {
    // if shop results change - reset and update the shop markers and move map center to 1st item in list
    if (
      _get(changes, 'shopSearchResults.currentValue') &&
      !changes.shopSearchResults.isFirstChange()
    ) {
      switch (this.currentMapRedrawTriggerState) {
        case MAP_TRIGGER.ON_SEARCH:
          if (this.geocoder) {
            this.redrawMapOnSearch(this.searchedValue);
          }
          break;
        case MAP_TRIGGER.ON_DRAG:
          this.redrawMapOnDrag();
          break;
        case MAP_TRIGGER.ON_PAGINATE:
          this.redrawMapOnPaginate();
          break;
        case MAP_TRIGGER.ON_ERROR:
          this.redrawMapOnError();
      }
    }

    // Toggle map interactivity when search type changes
    if (_get(changes, 'searchType.currentValue') && !changes.searchType.isFirstChange()) {
      if (this.searchMarker) {
        this.searchMarker.setMarkerDraggable(this.searchType === SEARCH_TYPES.NAME ? true : false);
      }
    }

    // When a shop is selected in the results list open the corresponding info window
    if (_get(changes, 'selectedShop.currentValue') && !changes.selectedShop.isFirstChange()) {
      const marker = this.shopMarkers.find(m => {
        return m.getShop() === this.selectedShop;
      });
      this.infoWindow.openInfoWinfowForMarker(
        marker.getMarker(),
        this.selectedShop,
        this.searchContext
      );
    }

    if (_get(changes, 'currentMapRedrawTriggerState.currentValue')) {
      if (this.currentMapRedrawTriggerState === MAP_TRIGGER.ON_ERROR) {
        this.redrawMapOnError();
      }
    }

    if (_get(changes, 'appState.currentValue')) {
      if (this.appState === APP_STATE.RESULTS_NOT_FOUND && this.geocoder) {
        this.redrawMapOnSearch(this.searchedValue);
        this.clearMarkers(this.shopMarkers);
      }
    }
  }

  private mapHasLoaded() {
    this.mapLoaded = true;
    this.cd.detectChanges();
  }

  private redrawMapOnSearch(searchedValue: SearchedValue) {
    this.getLatLngDistanceFromSearchedValue(searchedValue).subscribe(lastValue => {
      this.setLastSuccessfulSearchedValue.emit(lastValue);
      this.updateMapPosition(lastValue.lat, lastValue.lng);
    });
  }

  private redrawMapOnDrag() {
    if (this.shopSearchResults.length > 0) {
      this.setShopMarkers();
    } else {
      this.clearMarkers(this.shopMarkers);
    }
  }

  private redrawMapOnPaginate() {
    if (this.shopSearchResults.length > 0) {
      const coord = this.shopSearchResults[0].coordinates;
      const center = {
        lat: Number(coord.latitude + 0.015),
        lng: Number(coord.longitude + 0.015)
      };
      this.updateMapPosition(center.lat, center.lng);
    }
  }

  private redrawMapOnError() {
    this.clearMarkers(this.shopMarkers);
    if (this.appState === APP_STATE.OUT_OF_COUNTRY) {
      this.updateMapPosition(
        this.lastSuccessfulSearchedValue.lat,
        this.lastSuccessfulSearchedValue.lng
      );
    }
  }

  private updateMapPosition(lat: number, lng: number) {
    this.overlay.removePulse();
    this.map.setCenter(new google.maps.LatLng(lat, lng));
    this.searchMarker.setPosition(
      {
        lat: lat,
        lng: lng
      },
      this.overlay
    );
    this.overlay.addPulse();
    this.setShopMarkers();
  }

  /**
   * Convert the address from an SearchedValue type to a concatenated string
   */
  private formatAddress(address): string {
    let fullAddress = address.addressLine1 || '';
    if (address.city) {
      fullAddress = fullAddress + ' ' + address.city;
    }
    if (address.state) {
      fullAddress = fullAddress + ' ' + address.state;
    }
    if (address.zip) {
      fullAddress = fullAddress + ' ' + address.zip;
    }
    return fullAddress;
  }

  /**
   * TODO: Update this comment to document the new functionality
   * Calls the Google Geocode API with the address as the parameter, returns the coordinates of the given address
   * and the status of the API request.
   * Given an OK status - set the map options and shopMarkers based on the coordinates
   * @param address full address combined in one string
   */
  private getLatLngDistanceFromSearchedValue(
    searchedValue: SearchedValue
  ): Observable<{ lat: number; lng: number; distance: string }> {
    // If the searchedValue already has lat/lng/distance, return it
    if (
      _has(searchedValue, 'lat') &&
      _has(searchedValue, 'lng') &&
      _has(searchedValue, 'distance')
    ) {
      return of({
        lat: searchedValue.lat,
        lng: searchedValue.lng,
        distance: searchedValue.distance
      });
    }

    // If we don't already have a lat / lng, call geocoder to get the coordinates from the address
    const formattedAddress = this.formatAddress(searchedValue);
    return from(this.geocoder.getCoordinatesFromAddress(formattedAddress)).pipe(
      map((results: any) => {
        return {
          lat: results.lat,
          lng: results.lng,
          distance: this.searchedValue.distance
        };
      })
    );

    // TODO: Add a catch
    /*
      .catch(err => {
        console.log(err);
        // TODO: This should really be an app state that says that we can't figure out how to center the map based on the search input
        this.setStateFromResults.emit(APP_STATE.RESULTS_NOT_FOUND);
      });
    */
  }

  /**
   * Create a new searched location indicator marker
   * Handle when the marker is dragged
   */
  private createSearchMarker(searchType: SEARCH_TYPES) {
    const opts: google.maps.MarkerOptions = {
      icon: this.images.getMapMarkerImage(),
      position: this.map.getCenter()
    };

    this.searchMarker = new MapMarker(this.map, opts);
    if (searchType === SEARCH_TYPES.NAME) {
      this.searchMarker.setMarkerDraggable(true);
    } else {
      this.searchMarker.setMarkerDraggable(false);
    }

    this.searchMarker.addDragEvents();

    // TODO: create new function
    /*
    this.searchMarker.dragStartedEvent().subscribe(() => {
      this.overlay.hidePulse();
    });

    this.searchMarker.dragEndedEvent().subscribe(newPos => {
      this.setMapRedrawTriggerState.emit(MAP_TRIGGER.ON_DRAG);
      if (newPos.moved) {
        const lat: number = newPos.event.latLng.lat();
        const lng: number = newPos.event.latLng.lng();
        this.map.panTo(newPos.event.latLng);
        this.geocoder.getAddressFromCoordinates(newPos.event.latLng)
          .then(results => {
            this.overlay.removePulse();
            // emit new coords to use as params for new drp search
            const lastValue: SearchedValue = {
              lat: lat,
              lng: lng,
              distance: this.searchedValue.distance
            };
            this.searchMarkerCoords.emit(lastValue);
            this.overlay.updateOverlayPosition(newPos.event);
            this.overlay.addPulse();
            const zip = _find(
              results,
              address => address.types[0] === 'postal_code'
            );
            const country = _find(
              results,
              address => address.types[0] === 'country'
            );

            // only update zip code if the location is in the US otherwise return it back to the previous state
            if (country && country.short_name === 'US') {
              // only if zip code was found update the map otherwise handle error
              if (zip) {
                this.userRelocatedZipCode.emit(zip.long_name);
                this.setLastSuccessfulSearchedValue.emit(lastValue);
              } else {
                this.setStateFromResults.emit(APP_STATE.NO_ZIP_FOUND);
              }
            } else {
              this.overlay.hidePulse();
              this.setStateFromResults.emit(APP_STATE.OUT_OF_COUNTRY);
              this.setMapRedrawTriggerState.emit(MAP_TRIGGER.ON_ERROR);
              this.redrawMapOnError();
            }
          })
          .catch(err => {
            console.log(err);
          });
      }
    });
    */
  }

  /**
   * Clear out all existing shop markers then for every shop search result found
   * create a new blue shop marker and create an associated info window
   */
  private setShopMarkers() {
    // clear out existing markers
    if (this.shopMarkers.length > 0) {
      this.clearMarkers(this.shopMarkers);
    }
    if (this.appState !== APP_STATE.RESULTS_NOT_FOUND && this.shopSearchResults) {
      // create map marker, info window and handle click events
      this.shopSearchResults.forEach(
        function (shop: SearchResult, index: number) {
          // Set up the marker label
          const label: google.maps.MarkerLabel = {
            color: 'white',
            fontFamily: 'Arial',
            fontSize: '14px',
            fontWeight: '500',
            text: (index + 1).toString()
          };

          // Set up the marker options
          const opts: google.maps.MarkerOptions = {
            position: new google.maps.LatLng(
              Number(shop.coordinates.latitude),
              Number(shop.coordinates.longitude)
            ),
            icon: {
              url: this.images.getMarkerImage(),
              scaledSize: new google.maps.Size(40, 40)
            },
            label: label,
            optimized: false
          };

          // Create the marker
          const marker = new MapMarker(this.map, opts, shop);

          // shop markers can be clicked to open a info window
          marker.addClickEvent();
          this.shopMarkers.push(marker);

          // handle when marker is clicked
          marker.markerClickedEvent$().subscribe(() => {
            this.infoWindow.openInfoWinfowForMarker(marker.getMarker(), shop, this.searchContext);
            this.setMapShopSelection.emit(shop);
          });
        }.bind(this)
      );
    }
  }

  /**
   * Clear off all of the shop markers on the map
   */
  private clearMarkers(markers) {
    markers.forEach((marker, index) => {
      markers[index].clearMarker();
    });
    this.shopMarkers = [];
  }
}
